<script setup lang="ts">
import { computed, defineEmits, defineProps, ref, RenderFunction, watch } from 'vue'
import BaseInputWrapper from './BaseInputWrapper.vue'
import { i18n } from '../../localization'
import { format } from 'date-fns'
import { parseDate } from '../../utilities/helpers'
import { useFieldHelper } from './useBaseInput'

const props = defineProps<{
    modelValue?: string
    label: string
    labelMuted?: string
    name: string
    icon?: RenderFunction
    required?: boolean
    required2?: boolean
    rules?: any
}>()

const labelMuted = computed(
    () =>
        props.labelMuted ||
        `( ${i18n.global.t('forms.dd')} | ${i18n.global.t('forms.mm')} | ${i18n.global.t('forms.yyyy')} )`
)

const field = useFieldHelper(props.name, props.rules, { initialValue: props.modelValue })
const { handleChange, errorMessage } = field

const emit = defineEmits(['update:modelValue'])

const year = ref(null)
const month = ref(null)
const day = ref(null)

const dayPadded = computed({
    get() {
        return pad(day.value)
    },
    set(val) {
        day.value = !isNaN(parseInt(val)) ? parseInt(val) : null
    },
})
const monthPadded = computed({
    get() {
        return pad(month.value)
    },
    set(val) {
        month.value = !isNaN(parseInt(val)) ? parseInt(val) : null
    },
})
const yearPadded = computed({
    get() {
        return pad(year.value, 4)
    },
    set(val) {
        // console.log(val, isNaN(val), isNaN(parseInt(val)), parseInt(val))
        year.value = !isNaN(parseInt(val)) ? parseInt(val) : null
    },
})

function pad(num, size = 2) {
    return num && !isNaN(num) ? ('000000' + num).slice(-size) : ''
}

const dateValue = computed(() => {
    if (year.value && month.value && day.value) {
        const date = new Date(Date.UTC(year.value, month.value - 1, day.value))
        return format(date, 'yyyy-MM-dd')
    }
    if (!year.value && !month.value && !day.value) {
        // set value to null if input is completely empty
        return null
    }
    // set value false to allow date validation rule
    return false
})

function onBlur() {
    updateValue(dateValue.value)
}

setDate(props.modelValue)

watch(
    () => props.modelValue,
    (v) => {
        setDate(v)
        handleChange(v)
    },
    { deep: true }
)
function updateValue(value) {
    handleChange(value)
    emit('update:modelValue', value)
}

function setDate(dateVal) {
    if (dateVal) {
        const date = parseDate(dateVal)

        if (date !== dateValue.value) {
            year.value = date.getFullYear()
            month.value = date.getMonth() + 1
            day.value = date.getDate()
        }
    }
}
</script>

<template>
    <BaseInputWrapper
        ref="inputWrapper"
        :name="props.name"
        :label="props.label"
        :label-muted="labelMuted"
        :icon="icon"
        :required="required"
        :required2="required2">
        <fieldset>
            <div class="-space-y-px rounded-md bg-white shadow-sm">
                <div class="flex flex-row -space-x-px">
                    <div class="basis-1/3">
                        <label :for="props.name + '_day'" class="sr-only">{{ $t('forms.day') }}</label>
                        <input
                            :id="props.name + '_day'"
                            v-model="dayPadded"
                            v-bind="$attrs"
                            type="text"
                            name="date-day"
                            :title="$t('forms.day')"
                            class="relative block w-full rounded-none rounded-bl-md rounded-tl-md border-gray-300 bg-transparent py-3 focus:z-10 focus:border focus:border-primary focus:ring-primary disabled:bg-gray-100 sm:text-sm"
                            :class="{
                                'pl-10': !!icon,
                                'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500':
                                    errorMessage,
                            }"
                            :placeholder="$t('forms.dd')"
                            @blur="onBlur" />
                    </div>
                    <div class="basis-1/3">
                        <label :for="props.name + '_month'" class="sr-only">{{ $t('forms.month') }}</label>
                        <input
                            :id="props.name + '_month'"
                            v-model="monthPadded"
                            v-bind="$attrs"
                            type="text"
                            min="1"
                            max="12"
                            name="date-month"
                            :title="$t('forms.month')"
                            class="relative block w-full rounded-none border-gray-300 bg-transparent py-3 focus:z-10 focus:border focus:border-primary focus:ring-primary disabled:bg-gray-100 sm:text-sm"
                            :class="{
                                'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500':
                                    errorMessage,
                            }"
                            :placeholder="$t('forms.mm')"
                            @blur="onBlur" />
                    </div>
                    <div class="basis-2/4">
                        <label :for="props.name + '_year'" class="sr-only">{{ $t('forms.year') }}</label>
                        <input
                            :id="props.name + '_year'"
                            v-model="yearPadded"
                            v-bind="$attrs"
                            type="text"
                            max="2100"
                            name="date-year"
                            :title="$t('forms.year')"
                            class="relative block w-full rounded-none rounded-br-md rounded-tr-md border-gray-300 bg-transparent py-3 focus:z-10 focus:border focus:border-primary focus:ring-primary disabled:bg-gray-100 sm:text-sm"
                            :class="{
                                'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500':
                                    errorMessage,
                            }"
                            :placeholder="$t('forms.yyyy')"
                            @blur="onBlur" />
                    </div>
                </div>
            </div>
        </fieldset>
    </BaseInputWrapper>
</template>

<style scoped></style>
